import React, { Fragment, useEffect, useRef, useState, useContext } from "react";
import AssetDetail from "./AssetDetails/AssetDetailsForm";
import { makeStyles } from "@material-ui/core/styles";
import {
	Box,
	Collapse,
	Divider,
	Grid,
	Link,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Delete from "@material-ui/icons/Delete";
import Add from "@material-ui/icons/Add";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import FeeDetail from "./FeeDetailsForm";
import Utils from "../Utils";
import ConfirmDialog from "../ConfirmDialog";
import useAssetProvider from "./useAssetProvider";
import NmefSpinner from "../NmefSpinner";
import { PickListDataAPI } from "../API/PickListDataAPI";
import { checkEditAccess } from '../Constants/utils';
import { DocsDataAPI } from "../API/DocsDataAPI";

const useStyles = makeStyles((theme) => ({
	root: {
		"& > *": {
			borderBottom: "unset",
			color: "white",
		},
	},

	EvenRow: {
		"& > *": {
			borderBottom: "unset",
			backgroundColor: "#022F40",
			color: "white",
		},
	},
	OddRow: {
		"& > *": {
			borderBottom: "unset",
			backgroundColor: "#4B4E6D",
			color: "white",
		},
	},
	tableBorder: {
		borderBottom: "2px solid white",
	},

	tableCellStyle: {
		borderRightWidth: "1px",
		borderRightColor: "#d2d2d2",
		borderRightStyle: "solid",
	},
	divider: {
		width: "100%",
		height: "2px",
	},
	rowItem: {
		paddingTop: theme.spacing(1),
	},
	feeIcon: {
		marginRight: 2,
		verticalAlign: "middle",
	},
	header: {
		display: "flex",
		alignItems: "center",
	},
	headerIcon: {
		position: "relative",
		marginRight: "5px",
	},
	headerTitle: {},
	feeIconText: {
		position: "absolute",
		top: "0.55rem",
		left: "0.7rem",
		color: "#FFFFFF",
	},
	truckIconText: {
		position: "absolute",
		top: "0.35rem",
		left: "0.5rem",
		color: "#FFFFFF",
	},
	incomplete: {
		"& > *": {
			borderBottom: "unset",
			backgroundColor: "rgb(86, 3, 15)",
			color: "white",
		},
	},
}));

const AssetTable = ({ onClickAssetAdd }) => {
	const {
		isReadOnly,
		app,
		assets,
		assetDocuments,
		fees,
		onAssetDelete,
		onFeeDelete,
		setFeeTypeList,
		feeTypes
	} = useAssetProvider();
	const classes = useStyles();
	const [summaryData, setSummaryData] = useState({});
	const [tableData, setTableData] = useState([]);
	const [tableItemMap, setTableItemMap] = useState({ id: "", open: false });
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isFee, setIsFee] = useState(false);
	const deleteId = useRef(null);
	const [isLoading, setLoading] = useState(false);
	const [dealDataList, setDealDataList] = useState([]);

	useEffect(() => {
		(async () => {
			if (app) {
				let dealData = await DocsDataAPI.fetchApplicationDealsById(
					app.id,
					app.broker_id,
					app.locked_by_email
				);
				if (dealData) {
					setDealDataList(dealData);
				}
			}
		})();
	}, [app]);

	const handelCollapse = (row) => {
		if (tableItemMap?.id == row.id) {
			setTableItemMap({
				id: "", open: false
			})
		} else {
			setTableItemMap({
				id: row.id, open: true
			})
		}
	}

	useEffect(() => {
		(async () => {
			if (assets || fees) {
				setLoading(true);
				const mapedFees = [
					...fees.filter((fee) => fee.payable_to !== "Lessor"),
					...fees.filter((fee) => fee.payable_to === "Lessor"),
				];
				setTableData([...assets, ...mapedFees]);

			}
			if (app && assets && fees && dealDataList) {
				const assetValue = assets
					.filter((asset) => {
						return !asset.is_trade_in && !asset.is_collateral;
					})
					.reduce((total, item) => {
						return total + parseFloat(item.price || 0);
					}, 0.0);

				const feeValue = fees
					.filter((fee) => fee.payable_to !== "Lessor" && !fee.is_blind)
					.reduce((total, item) => {
						return total + parseFloat(item.primary_amount || 0);
					}, 0.0);


				const refiDeal = dealDataList && dealDataList.find((deal) => deal?.is_refi === true);
				const assetWithOverride = assets && assets.find((asset) => asset?.asset_value_override);
				const refiAdvance = refiDeal ? refiDeal.Advance : 0;

				const assetOverride = assetWithOverride ? (assetWithOverride.asset_value_override) : 0;
				const invoiceTotal = refiAdvance * assetOverride;
				setSummaryData({
					name: "Summary",
					assetValue: assetValue,
					feeValue: feeValue,
					total: assetValue + feeValue,
					invoiceTotal: invoiceTotal || app.invoice_amount,
					reconValue: app.invoice_amount - (assetValue + feeValue),
				});
			}
			if (feeTypes && feeTypes?.length <= 0) {
				try {
					const response = await PickListDataAPI.getPickList({
						object: "Fee__c",
						field: "Type__c",
					})

					const feeTypeValues = [];
					for (let i = 0; i < response.length; i++) {

						feeTypeValues.push({
							label: response[i],
							value: response[i],
						});
					}
					feeTypeValues.sort((a, b) => a.value.localeCompare(b.value))
					setFeeTypeList(feeTypeValues)
				} catch {
					console.log(" Failed to fetch asset types ")
				}
			}
			setLoading(false);
		})();
	}, [app, assets, fees, dealDataList]);

	const deleteItem = () => {
		if (isFee && deleteId.current) onFeeDelete(deleteId.current);
		else if (!isFee && deleteId.current) onAssetDelete(deleteId.current);
		deleteId.current = null;
		return;
	};
	
	const headerTag = (asset) => {
		if (asset?.is_trade_in && !asset?.is_collateral) return "(Trade In)";
		else if (!asset?.is_trade_in && asset?.is_collateral) return "(Collateral)";
		else if (asset?.is_cashout) return "(Cash Out)";
		else return "";
	};

	const generateDetails = (rowDetails) => {
		let overrideDetails = "";
		if (rowDetails?.override_year || rowDetails?.year) {
			overrideDetails = rowDetails?.override_year ?? rowDetails?.year;
		}
		if (rowDetails?.override_make || rowDetails?.make) {
			overrideDetails = overrideDetails + " " + (rowDetails?.override_make ?? rowDetails?.make);
		}
		if (rowDetails?.override_model || rowDetails?.model) {
			overrideDetails = overrideDetails + " " + (rowDetails?.override_model ?? rowDetails?.model);
		}
		if (rowDetails?.override_vin) {
			overrideDetails = overrideDetails + " " + rowDetails?.override_vin;
		}
		if (rowDetails?.override_serial) {
			overrideDetails = overrideDetails + " " + rowDetails?.override_serial;
		}
		let originalDetails = "";
		if (rowDetails?.year) {
			originalDetails = rowDetails?.year;
		}
		if (rowDetails?.make) {
			originalDetails = originalDetails + " " + rowDetails?.make;
		}
		if (rowDetails?.model) {
			originalDetails = originalDetails + " " + rowDetails?.model;
		}
		if (rowDetails?.vin && !originalDetails.includes(rowDetails?.vin)) {
			originalDetails = originalDetails + " " + rowDetails?.vin;
			if (overrideDetails !== "" && !rowDetails?.override_vin) {
				overrideDetails = overrideDetails + " " + rowDetails?.vin;
			}
		}
		if (rowDetails?.serial && !originalDetails.includes(rowDetails?.serial)) {
			originalDetails = originalDetails + " " + rowDetails?.serial;
			if (overrideDetails !== "" && !rowDetails?.override_serial) {
				overrideDetails = overrideDetails + " " + rowDetails?.serial;
			}
		}
		if (overrideDetails === "") {
			return originalDetails;
		}
		return overrideDetails;
	};

	function Row({ row, assetDocuments, styleProp }) {
		const feeColor = row.payable_to === "Lessor" ? "#8d4e00" : "#468847";
		const feeText = row.payable_to === "Lessor" ? "N" : "F";
		return (
			<Fragment>
				<TableRow
					className={
						row?.is_incomplete
							? classes.incomplete
							: styleProp === "EvenRow"
								? classes.EvenRow
								: classes.OddRow
					}
					onClick={() => handelCollapse(row)

					}>
					<TableCell style={{ padding: 7 }}>
						<IconButton aria-label="expand row" size="small">
							{tableItemMap?.id == row.id && tableItemMap.open ? (
								<KeyboardArrowUpIcon style={{ color: "white" }} />
							) : (
								<KeyboardArrowDownIcon style={{ color: "white" }} />
							)}
						</IconButton>
					</TableCell>
					<TableCell
						className={classes.tableCellStyle}
						style={{ padding: 7, width: 210 }}>
						{row?.name?.includes("A-") ? (
							<div className={classes.header}>
								<div className={classes.headerIcon}>
									<LocalShippingIcon
										className={classes.feeIcon}
										fontSize="large"
										style={{ color: "#468847" }}
									/>
									<span className={classes.truckIconText}>$</span>
								</div>
								<div className={classes.headerTitle}>
									<span style={{ fontSize: "1rem" }}>
										<Link component="button">
											<span style={{ color: "#0072E5" }}>
												{row.name}
											</span>
										</Link>
									</span>
									<br />
									<span style={{ fontSize: "1rem" }}>
										<Link component="button">
											<span style={{ color: "#0072E5" }}>
												{row.type}
											</span>
										</Link>
									</span>
									<br />
									<span
										style={{ color: "#468847", fontWeight: "bold", fontSize: "0.75rem" }}>
										{headerTag(row)}
									</span>
								</div>
							</div>
						) : (
							<div className={classes.header}>
								<div className={classes.headerIcon}>
									<InsertDriveFileIcon
										className={classes.feeIcon}
										fontSize="large"
										style={{ color: feeColor }}
									/>
									<span className={classes.feeIconText}>{feeText}</span>
								</div>
								<div className={classes.headerTitle}>
									<Link component="button">
										<span style={{ color: "#0072E5", fontSize: "1rem" }}>
											{row.id}
										</span>
									</Link>
									<br />
									<Link component="button">
										<span style={{ color: "#0072E5" }}>
											{row.type}
										</span>
									</Link>
								</div>
							</div>
						)}
					</TableCell>
					<TableCell align="center" style={{ padding: 7 }}>
						{row?.id?.includes("Fee-") ?
							row.vendor
							// (row.vendor?.includes("ACC-") ? row.vendor : "-")
							: row.vendor_id}
					</TableCell>
					<TableCell align="center" style={{ padding: 7 }}>
						{row?.id?.includes("Fee-") ? row.vendor_name : row.vendor_name || row.vendor}
					</TableCell>
					<TableCell align="center" style={{ padding: 7 }}>
						{row?.id?.includes("Fee-")
							? row.type
								? row.type + "\n"
								: "" + (row.payable_to || "")
							: generateDetails(row)}
					</TableCell>
					<TableCell align="center" style={{ padding: 7 }}>
						{"$" +
							Utils.formatCurrencyNumber(
								row?.id?.includes("Fee-") ? row.primary_amount : row.price
							)}
					</TableCell>
					<TableCell align="center" style={{ padding: 7 }}>
						{row?.id?.includes("Fee-")
							? ""
							: "$" + Utils.formatCurrencyNumber(row.asset_value_override)}
					</TableCell>

					{row?.id?.includes("Fee-") ? (
						<TableCell></TableCell>
					) : (
						<TableCell
							align="center"
							style={{
								padding: 7,
								color:
									Utils.formatCurrencyNumber(row.ltv) <= 80
										? "red"
										: "green",
							}}>
							{Utils.formatCurrencyNumber(row.ltv)} %
						</TableCell>
					)}
					<TableCell align="center" style={{ padding: 7 }}>
						{!isReadOnly && checkEditAccess() && (
							<div>
								<IconButton
									size="small"
									onClick={() => {
										setIsFee(row?.id?.includes("Fee-") ? true : false);
										setConfirmOpen(true);
										deleteId.current = row.id;
									}}>
									<Delete style={{ color: "red" }} />
								</IconButton>
							</div>
						)}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell
						align="center"
						style={{ paddingBottom: 0, paddingTop: 0 }}
						colSpan={8}>
						<Collapse in={tableItemMap?.id == row.id && tableItemMap.open} timeout="auto" unmountOnExit>
							<Box p={1}>
								{row?.id?.includes("Fee-") ? (
									<FeeDetail fee={row} />
								) : (
									<AssetDetail asset={row} assetDocuments={assetDocuments} handelCollapse={(row) => { handelCollapse(row) }} />
								)}
							</Box>
						</Collapse>
					</TableCell>
				</TableRow>
			</Fragment>
		);
	}

	return (
		<div>
			{isLoading ? (
				<div style={{ padding: "20px" }}>
					<NmefSpinner />
				</div>
			) : (
				<>
					<ConfirmDialog
						open={confirmOpen}
						setOpen={setConfirmOpen}
						title="Delete Asset?"
						content="Are you sure you want to delete this asset?"
						onConfirm={() => deleteItem()}
					/>
					<TableContainer component={Paper}>
						<Table aria-label="collapsible table">
							<TableHead className={classes.tableBorder}>
								<TableRow className={classes.EvenRow}>
									<TableCell align="center"></TableCell>
									<TableCell align="center"></TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											Vendor ID
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											Vendor
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										{" "}
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											Details
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											Selling $
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											Advised $
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										<Typography variant="h6" style={{ fontSize: "1.00rem" }}>
											LTV
										</Typography>
									</TableCell>
									<TableCell align="center" style={{ padding: 10 }}>
										{!isReadOnly && checkEditAccess() && (
											<IconButton
												style={{ backgroundColor: "#52BE80" }}
												size="small"
												onClick={() => onClickAssetAdd(true)}>
												<Add style={{ color: "white" }} />
											</IconButton>
										)}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{tableData.map((row, i) => (
									<Row
										key={row.id}
										row={row}
										assetDocuments={assetDocuments}
										styleProp={i % 2 === 0 ? "EvenRow" : "OddRow"}
									/>
								))}

								{/* Summary Row  START*/}
								<TableRow className={classes.EvenRow}>
									<TableCell></TableCell>
									<TableCell className={classes.tableCellStyle}>
										<Typography variant="subtitle1">Summary</Typography>
									</TableCell>
									<TableCell colSpan="7">
										<Grid container direction="column" alignItems="flex-start">
											<Grid
												container
												item
												justifyContent="space-between"
												className={classes.rowItem}>
												<Typography variant="subtitle1">Assets:</Typography>
												<Typography variant="body1">
													{"$" + Utils.formatCurrencyNumber(summaryData.assetValue)}
												</Typography>
											</Grid>
											<Grid
												container
												item
												justifyContent="space-between"
												className={classes.rowItem}>
												<Typography variant="subtitle1">Fees:</Typography>
												<Typography variant="body1">
													{"$" + Utils.formatCurrencyNumber(summaryData.feeValue)}
												</Typography>
											</Grid>
											<Divider className={classes.divider} />

											<Grid
												container
												item
												justifyContent="space-between"
												className={classes.rowItem}>
												<Typography variant="subtitle1">Total:</Typography>

												<Typography variant="body1">
													{"$" + Utils.formatCurrencyNumber(summaryData.total)}
												</Typography>
											</Grid>

											<Divider className={classes.divider} />

											<Grid
												container
												item
												justifyContent="space-between"
												className={classes.rowItem}>
												<Typography variant="subtitle1">Invoice Total:</Typography>
												<Typography variant="body1">
													{"$" + Utils.formatCurrencyNumber(summaryData.invoiceTotal)}
												</Typography>
											</Grid>
											<Grid
												container
												item
												justifyContent="space-between"
												className={classes.rowItem}>
												<Typography variant="subtitle1">Reconciliation Amount:</Typography>
												<Typography
													variant="body1"
													style={{
														color: summaryData.reconValue === 0 ? "green" : "red",
													}}>
													{"$" + Utils.formatCurrencyNumber(summaryData.reconValue)}
												</Typography>
											</Grid>
										</Grid>
									</TableCell>
								</TableRow>
								{/* summary Row  END*/}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}
		</div>
	);
};
export default AssetTable;
